import { Injectable, inject, signal, computed, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';
import { ELocalStorage } from '../enums/ELocalStorage';
import { EModelApiPaths } from '../enums/EModelApiPaths';
import { Product } from '../../interfaces/product.interface';

@Injectable({ providedIn: 'root' })
export class WishlistService {
  private api = inject(ApiService);
  private auth = inject(AuthService);
  private toast = inject(ToastService);
  private platformId = inject(PLATFORM_ID);

  wishlistIds = signal<string[]>([]);
  wishlistItems = signal<Product[]>([]);
  wishlistCount = computed(() => this.wishlistIds().length);

  init(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    if (this.auth.isLoggedIn()) {
      this.fetchWishlist();
    } else {
      const stored = localStorage.getItem(ELocalStorage.WISHLIST);
      if (stored) {
        try {
          this.wishlistIds.set(JSON.parse(stored));
        } catch {
          localStorage.removeItem(ELocalStorage.WISHLIST);
        }
      }
    }
  }

  fetchWishlist(): void {
    this.api.callCustomApi<{ data: { items: any[] } }>(EModelApiPaths.WISHLIST).subscribe({
      next: (res) => {
        const items = res?.data?.items || [];
        this.wishlistIds.set(items.map((i) => String(i.product_id)));
        this.wishlistItems.set(items.map((i) => ({
          _id: String(i.product_id),
          title: i.title,
          slug: i.slug,
          cover_image: i.cover_image,
          rate: i.rate,
          mrp: i.mrp,
          color_primary: i.color_primary,
          is_active: i.is_active,
        } as Product)));
      },
    });
  }

  isInWishlist(productId: string): boolean {
    return this.wishlistIds().includes(productId);
  }

  toggleWishlist(productId: string): void {
    if (this.isInWishlist(productId)) {
      this.removeFromWishlist(productId);
    } else {
      this.addToWishlist(productId);
    }
  }

  addToWishlist(productId: string): void {
    if (this.auth.isLoggedIn()) {
      this.api.callCustomApi(EModelApiPaths.WISHLIST_TOGGLE, 'POST', { product_id: productId }).subscribe({
        next: () => {
          this.wishlistIds.update((ids) => [...ids, productId]);
          this.fetchWishlist();
          this.toast.success('Added to wishlist');
        },
      });
    } else {
      this.wishlistIds.update((ids) => [...ids, productId]);
      this.saveToLocal();
      this.toast.success('Added to wishlist');
    }
  }

  removeFromWishlist(productId: string): void {
    if (this.auth.isLoggedIn()) {
      this.api.callCustomApi(EModelApiPaths.WISHLIST_TOGGLE, 'POST', { product_id: productId }).subscribe({
        next: () => {
          this.wishlistIds.update((ids) => ids.filter((id) => id !== productId));
          this.wishlistItems.update((items) => items.filter((item) => item._id !== productId));
          this.toast.info('Removed from wishlist');
        },
      });
    } else {
      this.wishlistIds.update((ids) => ids.filter((id) => id !== productId));
      this.saveToLocal();
      this.toast.info('Removed from wishlist');
    }
  }

  private saveToLocal(): void {
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(ELocalStorage.WISHLIST, JSON.stringify(this.wishlistIds()));
    }
  }
}
